import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, KeyRound, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MPIN Input component - 4 digit PIN
function MPINInput({ value, onChange, placeholder = "Enter 4-digit MPIN" }) {
  return (
    <Input
      type="password"
      inputMode="numeric"
      maxLength={4}
      pattern="[0-9]*"
      data-testid="admin-mpin-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
        onChange(val);
      }}
      className="text-center tracking-[0.5em] text-lg font-mono"
    />
  );
}

export default function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotMPIN, setShowForgotMPIN] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [newMPIN, setNewMPIN] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [testOTP, setTestOTP] = useState('');
  const [form, setForm] = useState({ email: '', mpin: '' });

  // Handle Google OAuth callback - Super Admin uses the /api/super-admin endpoint
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [location]);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      // For super admin, we need to verify the Google user is actually a super admin
      // First get the Google user data
      const googleRes = await axios.post(`${API_URL}/api/auth/google-auth`, { session_id: sessionId });
      
      if (googleRes.data.is_new) {
        toast.error('This Google account is not registered as a Super Admin');
      } else if (googleRes.data.user?.role !== 'super_admin') {
        toast.error('Access denied. Super admin credentials required.');
      } else {
        localStorage.setItem('super_admin_token', googleRes.data.token);
        localStorage.setItem('super_admin_user', JSON.stringify(googleRes.data.user));
        toast.success('Welcome back!');
        navigate('/admin/dashboard');
      }
    } catch (err) {
      const _d1 = err.response?.data?.detail; toast.error(typeof _d1 === 'string' ? _d1 : 'Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/super-admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.email) {
      toast.error('Please enter your email');
      return;
    }
    
    if (!form.mpin || form.mpin.length < 4) {
      toast.error('Please enter your MPIN');
      return;
    }
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/super-admin/login`, {
        email_or_phone: form.email,
        mpin: form.mpin
      });
      
      localStorage.setItem('super_admin_token', res.data.token);
      localStorage.setItem('super_admin_user', JSON.stringify(res.data.user));
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : '';
      
      if (errorMessage.toLowerCase().includes('no account found')) {
        toast.error('No account found with this email or phone.');
      } else if (errorMessage.toLowerCase().includes('incorrect mpin')) {
        toast.error('Incorrect MPIN. Please try again.');
      } else if (errorMessage.toLowerCase().includes('reset your mpin')) {
        toast.error('Please reset your MPIN using the "Forgot MPIN" option.');
      } else {
        toast.error(errorMessage || 'Authentication failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotMPIN = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/super-admin/forgot-mpin?email=${encodeURIComponent(forgotEmail)}${forgotPhone ? `&phone=${encodeURIComponent(forgotPhone)}` : ''}`);
      setOtpSent(true);
      setTestOTP(res.data.otp_for_testing || '');
      toast.success('OTP sent to your email & WhatsApp');
    } catch (err) {
      const _d2 = err.response?.data?.detail; toast.error(typeof _d2 === 'string' ? _d2 : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetMPIN = async () => {
    if (newMPIN.length !== 4) {
      toast.error('Please enter a 4-digit MPIN');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/super-admin/reset-mpin?email=${encodeURIComponent(forgotEmail)}&otp=${forgotOTP}&new_mpin=${newMPIN}`);
      toast.success('MPIN reset successfully! Please login.');
      setShowForgotMPIN(false);
      setOtpSent(false);
      setForgotEmail('');
      setForgotOTP('');
      setNewMPIN('');
    } catch (err) {
      const _d3 = err.response?.data?.detail; toast.error(typeof _d3 === 'string' ? _d3 : 'Failed to reset MPIN');
    } finally {
      setLoading(false);
    }
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white">Completing Google Sign In...</p>
        </div>
      </div>
    );
  }

  // Forgot MPIN Modal
  if (showForgotMPIN) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md md:max-w-lg bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Reset MPIN</CardTitle>
            <CardDescription>
              {!otpSent ? 'Enter your email to receive an OTP' : 'Enter OTP and your new 4-digit MPIN'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!otpSent ? (
              <>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="admin@1clin.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    data-testid="admin-forgot-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number <span className="text-muted-foreground text-xs">(for WhatsApp OTP)</span></Label>
                  <Input
                    type="tel"
                    placeholder="+91 9876543210"
                    value={forgotPhone}
                    onChange={(e) => setForgotPhone(e.target.value)}
                    data-testid="admin-forgot-phone"
                  />
                </div>
                <Button
                  onClick={handleForgotMPIN}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  disabled={loading}
                  data-testid="admin-forgot-send-otp"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send OTP
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>OTP</Label>
                  <Input
                    type="text"
                    placeholder="6-digit OTP"
                    value={forgotOTP}
                    onChange={(e) => setForgotOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center tracking-widest"
                    maxLength={6}
                  />
                  {testOTP && <p className="text-xs text-slate-500">Test OTP: {testOTP}</p>}
                </div>
                <div className="space-y-2">
                  <Label>New MPIN (4 digits)</Label>
                  <MPINInput value={newMPIN} onChange={setNewMPIN} placeholder="Enter new MPIN" />
                </div>
                <Button
                  onClick={handleResetMPIN}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset MPIN
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setShowForgotMPIN(false);
                setOtpSent(false);
              }}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid-white/5 opacity-40"></div>
      
      <Card className="w-full max-w-md md:max-w-lg relative z-10 bg-white/95 backdrop-blur shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Super Admin Portal
          </CardTitle>
          <CardDescription className="text-slate-500">
            1Clin Platform Administration
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Google Sign In Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-300 hover:bg-slate-50"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or continue with MPIN</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                data-testid="admin-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@1clin.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mpin" className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-500" />
                MPIN (4 digits)
              </Label>
              <MPINInput
                value={form.mpin}
                onChange={(val) => setForm({ ...form, mpin: val })}
              />
            </div>

            <Button
              type="submit"
              data-testid="admin-submit-btn"
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
            
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => setShowForgotMPIN(true)}
                className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
              >
                Forgot MPIN?
              </button>
            </div>
          </form>

          <div className="mt-4 pt-4 border-t text-center">
            <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
              Back to Doctor Portal
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
